import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import RequestListFilters from 'components/base/request-list-filters';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { debounce } from 'utils/functions';
import { RequestType } from 'models/item-request.model';
import useDashboardContext from 'contexts/dashboard.context';
import KitchenProcurementCard from 'components/base/procurement-card/kitchenProcurementCard';

export type ExtendedRequestType = ENUM_REQUEST_STATUS;

type RequestStatusFilter = ENUM_REQUEST_STATUS;

export type FilterType = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: { startDate: Date; endDate: Date };
  current_page: number;
};
const ItemRequests = () => {
  const [itemRequests, setItemRequests] = useState<RequestType[]>();
  const [filters, setFilters] = useState<FilterType>({
    search: '',
    status: [],
    dateRange: {
      startDate: new Date(new Date().getFullYear() - 20),
      endDate: new Date()
    },
    current_page: 1
  });
  const [isFetchingItemsRequest, setIsFetchingItemsRequest] = useState(false);
  const [lastPage, setLastPage] = useState(0);
  const { activeStore } = useDashboardContext();

  const fetchItemsRequest = async (
    startDate: Date,
    endDate: Date,
    code: string,
    page: number,
    statusValues: RequestStatusFilter[]
  ) => {
    if (!activeStore?.id) return;
    try {
      setIsFetchingItemsRequest(true);

      const normalizedStatus = statusValues;
      const statusParam =
        normalizedStatus.length === 0
          ? undefined
          : normalizedStatus.length === 1
          ? normalizedStatus[0]
          : normalizedStatus;

      const response = await apiService.get(`kitchen-store/${activeStore.id}/requests`, {
        version: 'v2',
        params: {
          start_date: startDate,
          end_date: endDate,
          code,
          status: statusParam,
          page,
          store_id: activeStore.id
        }
      });
      if (response) {
        setItemRequests(response?.data?.data?.data);
        setLastPage(Math.ceil(response?.data?.data?.pagination.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching kitchen requests'}
          type='error'
        />
      );
    } finally {
      setIsFetchingItemsRequest(false);
    }
  };

  const debounceFetchItemRequests = useCallback(debounce(fetchItemsRequest, 1000), []);

  useEffect(() => {
    setIsFetchingItemsRequest(true);
    setItemRequests([]);
    debounceFetchItemRequests(
      filters.dateRange.startDate,
      filters.dateRange.endDate,
      filters.search,
      filters.current_page,
      filters.status
    );
  }, [filters, activeStore?.id]);

  return (
    <div>
      <PageHeaderText title='Manage Requests' description='Manage kitchen item requests' />
      <RequestListFilters
        statusMode='multi'
        statusOptions={[
          { text: ENUM_REQUEST_STATUS.APPROVED, value: ENUM_REQUEST_STATUS.APPROVED },
          { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },

          { text: ENUM_REQUEST_STATUS.SENT, value: ENUM_REQUEST_STATUS.SENT },
          { text: ENUM_REQUEST_STATUS.REJECTED, value: ENUM_REQUEST_STATUS.REJECTED },
          { text: ENUM_REQUEST_STATUS.COMPLETED, value: ENUM_REQUEST_STATUS.COMPLETED }
        ]}
        filters={filters}
        onFilterChange={setFilters}
        pageCount={lastPage}
      />
      {isFetchingItemsRequest ? (
        <div className='mt-10'>
          <ActivityIndicator />
        </div>
      ) : (
        <div className='mt-10 gap-4 grid grid-cols-3 max-[1250px]:grid-cols-2 max-[768px]:grid-cols-1'>
          {itemRequests?.map((p, i) => (
            <KitchenProcurementCard key={i} procurement={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemRequests;
